/*
 * This file is part of the Lundhs Realstone 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import objectFitImages from 'object-fit-images';
import picturefill from 'picturefill';

/**
 * This is the ResponsiveImage component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function ResponsiveImage({
  className,
  style,
  srcset,
  altText,
  description,
  src,
  sizes,
  sources,
  eager,
  onClick,
  ariaHidden,
}) {
  const fitImage = useCallback((node) => {
    if (node) {
      picturefill({
        elements: [node],
      });
      objectFitImages(node);
    }
  }, []);

  if (sources) {
    return (
      <picture className={className} style={style}>
        {Object.keys(sources).map((key) => (
          <source
            key={sources[key].media ? sources[key].media : key}
            srcSet={sources[key].srcset.join(', ')}
            media={sources[key].media ? sources[key].media : null}
            sizes={sources[key].sizes ? sources[key].sizes.join(', ') : null}
          />
        ))}
        <img
          src={src}
          alt={altText}
          ref={fitImage}
          loading={eager ? 'eager' : 'lazy'}
          data-pin-description={description}
          onClick={onClick}
          aria-hidden={ariaHidden ? true : null}
        />
      </picture>
    );
  }

  return (
    <img
      className={className}
      style={style}
      ref={fitImage}
      srcSet={srcset ? srcset.join(', ') : null}
      sizes={sizes ? sizes.join(', ') : null}
      src={src}
      alt={altText}
      loading={eager ? 'eager' : 'lazy'}
      data-pin-description={description}
      onClick={onClick}
      aria-hidden={ariaHidden ? true : null}
    />
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ResponsiveImage.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      srcset: PropTypes.arrayOf(PropTypes.string),
      sizes: PropTypes.arrayOf(PropTypes.string),
      media: PropTypes.string,
    })
  ),
  src: PropTypes.string,
  srcset: PropTypes.arrayOf(PropTypes.string),
  sizes: PropTypes.arrayOf(PropTypes.string),
  altText: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  eager: PropTypes.bool,
  onClick: PropTypes.func,
  ariaHidden: PropTypes.bool,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
ResponsiveImage.defaultProps = {
  sources: null,
  src: null,
  srcset: null,
  sizes: null,
  altText: null,
  description: null,
  className: null,
  style: null,
  eager: false,
  onClick: null,
  ariaHidden: false,
};

export default ResponsiveImage;
